import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/supabase';
import { useAuth } from './useAuth';
import { logService } from '../lib/logService';
import type { Subscription } from '../types/forms';

// =====================================================
// CONTEXTO DE ASSINATURA - ONEFORM
// =====================================================

interface SubscriptionContextType {
  subscription: Subscription | null;
  isTrialing: boolean;
  trialDaysLeft: number;
  isActive: boolean;
  isExpired: boolean;
  isExempt: boolean;
  needsSetup: boolean;
  loading: boolean;
  redirectToCheckout: (priceId: string, trialDays?: number) => Promise<void>;
  redirectToPortal: () => Promise<void>;
  /**
   * Schedule downgrade ao fim do período atual (v1.17.3).
   * Chama edge function cancel-subscription com cancel_at_period_end=true.
   */
  scheduleDowngrade: (targetTier: 'free' | 'pro') => Promise<{ error: string | null }>;
  /**
   * Cancela um downgrade agendado (reverte cancel_at_period_end=false).
   */
  resumeSubscription: () => Promise<{ error: string | null }>;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [isExempt, setIsExempt] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSubscriptionData = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setTrialEndsAt(null);
      setIsExempt(false);
      setLoading(false);
      return;
    }

    try {
      // Fetch trial_ends_at from profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('trial_ends_at, is_exempt')
        .eq('id', user.id)
        .single();

      setTrialEndsAt(profile?.trial_ends_at || null);
      setIsExempt(profile?.is_exempt === true);

      // Fetch active subscription
      const { data: subs } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['active', 'trialing', 'past_due'])
        .order('created_at', { ascending: false })
        .limit(1);

      setSubscription(subs?.[0] || null);
    } catch (err) {
      logService.error('subscription.fetch', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  // Realtime subscription updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchSubscriptionData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchSubscriptionData]);

  // Derived state
  const now = new Date();
  const trialEnd = trialEndsAt ? new Date(trialEndsAt) : null;
  const hasActiveSubscription = subscription !== null && ['active', 'trialing', 'past_due'].includes(subscription.status);
  const isInTrial = trialEnd !== null && trialEnd > now && !hasActiveSubscription;
  const trialDaysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))) : 0;
  const isActive = hasActiveSubscription || isInTrial || isExempt;
  const isExpired = !loading && !isActive;
  // needsSetup: user is in local trial but hasn't added a card/subscription yet
  // exempt users must NOT see checkout — even if in trial without card
  const needsSetup = !loading && isInTrial && !hasActiveSubscription && !isExempt;

  const redirectToCheckout = useCallback(async (priceId: string, trialDays?: number) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const response = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        price_id: priceId,
        ...(trialDays ? { trial_period_days: trialDays } : {}),
      }),
    });

    const { url, error } = await response.json();
    if (error) {
      logService.error('subscription.checkout', typeof error === 'string' ? error : 'Checkout failed', { error_detail: error });
      return;
    }
    if (url) {
      window.location.href = url;
    }
  }, []);

  const scheduleDowngrade = useCallback(async (targetTier: 'free' | 'pro') => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { error: 'Not authenticated' };

    const response = await fetch(`${SUPABASE_URL}/functions/v1/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ cancel_at_period_end: true, target_tier: targetTier }),
    });

    const payload = await response.json();
    if (!response.ok || payload?.error) {
      const err = payload?.error || `HTTP ${response.status}`;
      logService.error('subscription.downgrade', String(err), { error_detail: payload });
      return { error: String(err) };
    }
    // Realtime channel em subscriptions vai refrescar state automaticamente
    await fetchSubscriptionData();
    return { error: null };
  }, [fetchSubscriptionData]);

  const resumeSubscription = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { error: 'Not authenticated' };

    const response = await fetch(`${SUPABASE_URL}/functions/v1/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ cancel_at_period_end: false, target_tier: null }),
    });

    const payload = await response.json();
    if (!response.ok || payload?.error) {
      const err = payload?.error || `HTTP ${response.status}`;
      logService.error('subscription.resume', String(err), { error_detail: payload });
      return { error: String(err) };
    }
    await fetchSubscriptionData();
    return { error: null };
  }, [fetchSubscriptionData]);

  const redirectToPortal = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const response = await fetch(`${SUPABASE_URL}/functions/v1/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': SUPABASE_ANON_KEY,
      },
    });

    const { url, error } = await response.json();
    if (error) {
      logService.error('subscription.portal', typeof error === 'string' ? error : 'Portal failed', { error_detail: error });
      return;
    }
    if (url) {
      window.location.href = url;
    }
  }, []);

  const value = useMemo<SubscriptionContextType>(() => ({
    subscription,
    isTrialing: isInTrial,
    trialDaysLeft,
    isActive,
    isExpired,
    isExempt,
    needsSetup,
    loading,
    redirectToCheckout,
    redirectToPortal,
    scheduleDowngrade,
    resumeSubscription,
    refresh: fetchSubscriptionData,
  }), [subscription, isInTrial, trialDaysLeft, isActive, isExpired, isExempt, needsSetup, loading, redirectToCheckout, redirectToPortal, scheduleDowngrade, resumeSubscription, fetchSubscriptionData]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
